import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const CancellationPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Link to="/">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
        </Link>

        <div className="bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-3xl font-bold mb-2">Cancellation Policy</h1>
          <p className="text-gray-600 mb-8">Effective Date: {new Date().toLocaleDateString()}</p>

          <div className="space-y-6 text-gray-700">
            <section>
              <h2 className="text-xl font-semibold mb-3 text-gray-900">1. Order Cancellation Window</h2>
              <p className="mb-2">You can cancel your order:</p>
              <ul className="list-disc ml-6 space-y-2">
                <li><strong>Before Processing:</strong> Free cancellation within 1 hour of placing order</li>
                <li><strong>After Processing:</strong> Contact customer service immediately</li>
                <li><strong>After Shipping:</strong> Use our <Link to="/return-policy" className="text-indigo-600 hover:underline">Return Policy</Link> instead</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3 text-gray-900">2. How to Cancel</h2>
              <ol className="list-decimal ml-6 space-y-2">
                <li>Log in to your DressPay account</li>
                <li>Go to "My Orders" section</li>
                <li>Find your order using the 5-digit confirmation code</li>
                <li>Click "Cancel Order" button (if available)</li>
                <li>Or email <a href="mailto:support@dresspay.com" className="text-indigo-600 hover:underline">support@dresspay.com</a> with your order number</li>
                <li>Or call <a href="tel:3464837337" className="text-indigo-600 hover:underline">346-483-7337</a></li>
              </ol>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3 text-gray-900">3. Cancellation Fees</h2>
              <ul className="list-disc ml-6 space-y-2">
                <li><strong>Within 1 Hour:</strong> No fee, full refund</li>
                <li><strong>Before Shipping:</strong> No fee, full refund</li>
                <li><strong>After Shipping:</strong> Cannot cancel; must use return process</li>
                <li><strong>Partial Orders:</strong> Can cancel unshipped items only</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3 text-gray-900">4. Refund Processing</h2>
              <p className="mb-2">When your cancellation is approved:</p>
              <ul className="list-disc ml-6 space-y-2">
                <li>Refund is processed immediately</li>
                <li>Money returned to original payment method</li>
                <li>Credit/Debit cards: 5-10 business days</li>
                <li>Apple Pay/Cash App: 3-5 business days</li>
                <li>Cryptocurrency: 1-3 business days</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3 text-gray-900">5. Non-Cancellable Orders</h2>
              <p className="mb-2">Orders cannot be cancelled if:</p>
              <ul className="list-disc ml-6 space-y-2">
                <li>Item has already shipped</li>
                <li>Item is out for delivery</li>
                <li>Order contains perishable goods that are being prepared</li>
                <li>Item is a custom or personalized order</li>
                <li>Item is marked as "Final Sale"</li>
              </ul>
              <p className="mt-3">In these cases, please use our <Link to="/return-policy" className="text-indigo-600 hover:underline">Return Policy</Link> after receiving the item.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3 text-gray-900">6. Partial Cancellations</h2>
              <p>If your order contains multiple items:</p>
              <ul className="list-disc ml-6 space-y-2 mt-2">
                <li>You can cancel individual items that haven't shipped</li>
                <li>Shipped items must be returned via return policy</li>
                <li>Partial refund will be issued for cancelled items</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3 text-gray-900">7. Seller-Initiated Cancellations</h2>
              <p className="mb-2">We may cancel your order if:</p>
              <ul className="list-disc ml-6 space-y-2">
                <li>Product is out of stock or discontinued</li>
                <li>Pricing error occurred on our website</li>
                <li>Unable to verify payment information</li>
                <li>Shipping address is incorrect or undeliverable</li>
                <li>Suspected fraudulent activity</li>
              </ul>
              <p className="mt-3">You'll receive immediate notification and full refund if we cancel your order.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3 text-gray-900">8. Cancellation Confirmation</h2>
              <p>Once your cancellation is processed:</p>
              <ul className="list-disc ml-6 space-y-2 mt-2">
                <li>You'll receive an email confirmation</li>
                <li>Confirmation will include refund details</li>
                <li>Expected refund date will be provided</li>
                <li>Order status will update to "Cancelled"</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3 text-gray-900">9. Contact Information</h2>
              <p className="mb-2">For cancellation assistance:</p>
              <ul className="list-none ml-0 space-y-1">
                <li><strong>Email:</strong> <a href="mailto:support@dresspay.com" className="text-indigo-600 hover:underline">support@dresspay.com</a></li>
                <li><strong>Phone:</strong> <a href="tel:3464837337" className="text-indigo-600 hover:underline">346-483-7337</a></li>
                <li><strong>Hours:</strong> Monday - Friday, 9 AM - 6 PM EST</li>
                <li><strong>Response Time:</strong> Within 2 hours during business hours</li>
              </ul>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CancellationPolicy;